import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import { LayoutProvider } from './LayoutContext';
import { useTheme } from '../theme/ThemeProvider';
import { MenuIcon } from '../components/ui/Icons';
import Skeleton from '../components/ui/Skeleton/Skeleton';
import ErrorBoundary from '../components/ErrorBoundary';
import styles from './AppLayout.module.css';

/**
 * Respaldo mientras llega el archivo de una pantalla diferida.
 *
 * 📌 No lleva fondo propio ni alto de pantalla: está **dentro** del shell, que ya pone los dos. El
 * respaldo de `routes.tsx` sí los necesita, porque cubre las rutas públicas que no tienen shell.
 *
 * Se usan bloques y no un spinner porque el archivo suele llegar en decenas de milisegundos, y un
 * spinner que aparece y desaparece tan rápido se lee como un parpadeo.
 */
const ContentFallback: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <Skeleton width={220} height={28} radius={10} />
    <Skeleton height={120} radius={16} />
    <Skeleton height={320} radius={16} />
  </div>
);

const COLLAPSE_KEY = 'dylo-v2-sidebar-collapsed';

const AppLayout: React.FC = () => {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleCollapse = React.useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, []);

  const location = useLocation();

  /**
   * Logo del header móvil.
   *
   * Se elige por tema, igual que en el sidebar: los dos archivos existen porque el logotipo
   * cambia de color, no solo de fondo. Aquí siempre va el **completo**, nunca el símbolo: el
   * header tiene ancho de sobra y el colapso es un concepto de escritorio.
   */
  const { resolvedTheme } = useTheme();
  const mobileLogoSrc =
    resolvedTheme === 'dark' ? '/dylo-logo-dark.svg' : '/dylo-logo-light.svg';

  /**
   * Drawer del sidebar en móvil.
   *
   * Deliberadamente **no** se guarda en `localStorage`, a diferencia de
   * `collapsed`: un menú que tapa la pantalla debe empezar cerrado siempre.
   */
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const closeMobile = React.useCallback(() => setMobileOpen(false), []);

  // Al cambiar de página el drawer se cierra: si no, quedaría tapando el
  // contenido al que el usuario acaba de navegar.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Escape cierra el drawer, y mientras está abierto se bloquea el scroll del
  // fondo para que el gesto no mueva la página de atrás.
  React.useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  return (
    <LayoutProvider>
      <div className={styles.shell}>
        <Sidebar
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          mobileOpen={mobileOpen}
          onCloseMobile={closeMobile}
        />

        {/* Fondo oscuro del drawer. Solo existe cuando está abierto, y tocarlo
            cierra: es el gesto que espera cualquiera en un menú móvil. */}
        {mobileOpen && (
          <button
            type="button"
            className={styles.backdrop}
            aria-label="Close menu"
            onClick={closeMobile}
          />
        )}

        <div className={styles.main}>
          {/**
           * Header visible SOLO en móvil.
           *
           * Va aquí y no en `Header` porque varias páginas ocultan ese header
           * (`setHideHeader`), y quedarse sin acceso al menú no es una opción. Es
           * **sticky**: en un teléfono se hace mucho scroll y volver arriba solo para abrir
           * el menú era una molestia constante.
           */}
          <header className={styles.mobileBar}>
            {/* Logo en marco naranja, a la misma altura que el botón. */}
            <div className={styles.mobileLogo}>
              <img className={styles.mobileLogoImg} src={mobileLogoSrc} alt="DYLO" />
            </div>

            <button
              type="button"
              className={styles.menuBtn}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              {/* Solo el icono: la hamburguesa es universal y un objetivo cuadrado se
                  acierta mejor con el pulgar. El nombre vive en `aria-label`. */}
              <MenuIcon size={26} />
            </button>
          </header>

          <Header />
          {/* keyed por ruta → cada cambio de página re-monta y anima la entrada */}
          <div className={styles.content} key={location.pathname}>
            {/**
             * 🔴 **El `Suspense` va AQUÍ, y ese es el arreglo del pantallazo blanco.**
             *
             * Estaba en `routes.tsx`, envolviendo **todas** las rutas — el layout incluido. Y un
             * `Suspense` reemplaza por su respaldo **todo su subárbol**, así que al pedir una pantalla
             * diferida desaparecían el sidebar, el encabezado y el fondo de la página: quedaba un
             * esqueleto suelto sobre el blanco del documento. Con tema oscuro el golpe era peor, porque
             * el blanco que aparecía no era el de ningún tema.
             *
             * Dentro del contenedor del contenido, lo único que se reemplaza es la zona de la pantalla.
             * El chasis se queda quieto y el cambio de módulo se lee como que está cargando algo, no
             * como que la aplicación se cayó y volvió.
             *
             * 📌 Sigue habiendo un `Suspense` en `routes.tsx`: las rutas públicas y el login viven
             * fuera de este layout y también son diferidas.
             */}
            {/**
             * 🔴 **La red de seguridad va DENTRO del layout, y esa colocación es la decisión.**
             *
             * Si envolviera toda la aplicación, un error de una pantalla se llevaría también el sidebar y el
             * encabezado — el usuario quedaría sin forma de irse a otro módulo, que es exactamente lo que hace
             * cerrar la pestaña. Aquí el chasis sobrevive: falla la zona del contenido y el menú sigue vivo.
             *
             * 📌 `resetKey` es la ruta. Sin eso el error se queda pegado: se pulsa otro módulo, la ruta cambia y se
             * sigue viendo el mismo mensaje, porque el boundary no se enteraría de que el subárbol ya es otro.
             *
             * ⚠️ Va **por fuera** del `Suspense`: un chunk que falla al cargar lanza desde dentro de `Suspense`, así
             * que si el boundary estuviera dentro no lo atraparía.
             */}
            <ErrorBoundary resetKey={location.pathname} label="AppLayout.content">
              <React.Suspense fallback={<ContentFallback />}>
                <Outlet />
              </React.Suspense>
            </ErrorBoundary>
          </div>
          <Footer />
        </div>
      </div>
    </LayoutProvider>
  );
};

export default AppLayout;
